import { useState, useEffect, useCallback } from 'react';
import { Mail, X, Loader2, Check, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface SendDocumentEmailModalProps {
  onClose: () => void;
  clientId: string | null;
  companyId: string;
  documentId?: string;
  b2bOrderId?: string;
  docLabel: string;
}

export default function SendDocumentEmailModal({
  onClose,
  clientId,
  companyId,
  documentId,
  b2bOrderId,
  docLabel,
}: SendDocumentEmailModalProps) {
  const [clientEmail, setClientEmail] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      setClientEmail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('inventory_clients')
        .select('email')
        .eq('id', clientId)
        .maybeSingle();
      if (cancelled) return;
      const email = data?.email || null;
      setClientEmail(email);
      setEmailInput(email ?? '');
      setEditMode(!email);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const isValidEmail = (e: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

  const handleSend = useCallback(async () => {
    const targetEmail = editMode ? emailInput.trim() : (clientEmail || emailInput.trim());
    if (!isValidEmail(targetEmail)) {
      setResult({ ok: false, msg: 'כתובת מייל לא תקינה' });
      return;
    }

    // Save email to client if it was entered/edited
    if (clientId && targetEmail !== clientEmail) {
      setSaving(true);
      const { error } = await supabase
        .from('inventory_clients')
        .update({ email: targetEmail })
        .eq('id', clientId);
      setSaving(false);
      if (error) {
        setResult({ ok: false, msg: 'שגיאה בשמירת כתובת המייל' });
        return;
      }
      setClientEmail(targetEmail);
    }

    setSending(true);
    setResult(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-document-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            Apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            document_id: documentId ?? undefined,
            b2b_order_id: b2bOrderId ?? undefined,
            to_email: targetEmail,
            company_id: companyId,
          }),
        },
      );

      const json = await res.json();
      if (!res.ok || json.error) {
        setResult({ ok: false, msg: json.error || 'שגיאה בשליחת המייל' });
      } else {
        setResult({ ok: true, msg: 'המייל נשלח בהצלחה!' });
      }
    } catch {
      setResult({ ok: false, msg: 'שגיאת רשת בשליחת המייל' });
    } finally {
      setSending(false);
    }
  }, [clientId, clientEmail, emailInput, editMode, documentId, b2bOrderId, companyId]);

  const targetEmail = editMode ? emailInput.trim() : (clientEmail || emailInput.trim());
  const canSend = isValidEmail(targetEmail) && !sending && !saving;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">שלח במייל</h3>
              <p className="text-xs text-gray-500 mt-0.5">{docLabel}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : !clientId ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                למסמך הזה לא משויך לקוח. הכנס כתובת מייל לשליחה:
              </p>
              <input
                type="email"
                dir="ltr"
                placeholder="email@example.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm
                           focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none
                           transition-all"
              />
            </div>
          ) : clientEmail && !editMode ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                המסמך יישלח לכתובת:
              </p>
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3">
                <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-sm font-medium text-gray-800 flex-1" dir="ltr">
                  {clientEmail}
                </span>
                <button
                  onClick={() => { setEditMode(true); setEmailInput(clientEmail); }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  שנה
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {!clientEmail ? (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-800">
                    ללקוח הזה אין כתובת מייל. הכנס כתובת והיא תישמר בפרטי הלקוח לצמיתות.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600">עדכן את כתובת המייל:</p>
              )}
              <input
                type="email"
                dir="ltr"
                placeholder="email@example.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm
                           focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none
                           transition-all"
                autoFocus
              />
            </div>
          )}

          {/* Result */}
          {result && (
            <div
              className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium
                ${result.ok
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
                }`}
            >
              {result.ok ? (
                <Check className="w-4 h-4 shrink-0" />
              ) : (
                <AlertTriangle className="w-4 h-4 shrink-0" />
              )}
              {result.msg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-3">
          {result?.ok ? (
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-xl font-semibold text-sm
                         bg-gray-100 hover:bg-gray-200 text-gray-700
                         transition-colors"
            >
              סגור
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl font-semibold text-sm
                           bg-gray-100 hover:bg-gray-200 text-gray-700
                           transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleSend}
                disabled={!canSend}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl
                           font-semibold text-sm text-white transition-colors
                           bg-blue-600 hover:bg-blue-700 active:bg-blue-800
                           disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {sending || saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {saving ? 'שומר...' : 'שולח...'}
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    שלח מייל
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
